import { useEffect, useState, type FormEvent } from "react";
import type {
  CodeProblem,
  CodeScreenState,
  PortalWording,
} from "@shared-core/domain/portal-sign-in/types";
import { Button } from "../primitives/button";
import { Card } from "../primitives/card";
import { InputOTP, InputOTPSlot } from "../primitives/input-otp";
import { Notice, SignInFrame } from "./SignInFrame";

const CODE_LENGTH = 6;

function problemText(problem: CodeProblem): string {
  switch (problem.kind) {
    case "wrong":
      return `The code isn't right. Check the newest email from us. You have ${problem.triesLeft} ${problem.triesLeft === 1 ? "try" : "tries"} left.`;
    case "locked":
      return `Five wrong codes have been entered. Wait ${problem.minutes} minutes, then ask for a new code.`;
    case "expired":
      return "The code has expired. Ask for a new code below.";
  }
}

/** Counts down to when a new code may be asked for. */
function useCountdown(seconds: number, restartKey: string): number {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const timer = setInterval(
      () => setLeft((current) => Math.max(0, current - 1)),
      1000,
    );
    return () => clearInterval(timer);
  }, [seconds, restartKey]);
  return left;
}

/** Seconds after sending before the screen says where the code comes from. */
const SLOW_AFTER_SECONDS = 10;

/** Counts the seconds since the screen opened. */
function useElapsed(restartKey: string): number {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    setElapsed(0);
    const timer = setInterval(() => setElapsed((current) => current + 1), 1000);
    return () => clearInterval(timer);
  }, [restartKey]);
  return elapsed;
}

export interface CodeScreenProps {
  state: CodeScreenState;
  wording: PortalWording;
  busy?: boolean;
  onSubmit(code: string): void;
  onResend(): void;
  onDifferentAddress(): void;
}

/** Type the 6-digit code from the email. The screen is the same for every address, so it reveals nothing about it. */
export function CodeScreen({
  state,
  wording,
  busy = false,
  onSubmit,
  onResend,
  onDifferentAddress,
}: CodeScreenProps) {
  const [code, setCode] = useState("");
  const secondsLeft = useCountdown(state.resendSeconds, state.caseId);
  const elapsed = useElapsed(state.caseId);
  const isSlow =
    state.problem === null &&
    state.waitedSeconds + elapsed >= SLOW_AFTER_SECONDS;
  const locked = state.problem?.kind === "locked";

  function submit(event: FormEvent) {
    event.preventDefault();
    if (code.length === CODE_LENGTH) onSubmit(code);
  }

  return (
    <SignInFrame
      eyebrow={wording.brand}
      title="Check your email"
      subtitle={
        <>
          We&apos;ve emailed <b>{state.address}</b>. Type the 6-digit code from
          that email.
        </>
      }
      footer="The code works for 10 minutes on any device. You can read the email on your phone and type the code here."
    >
      <Card className="gap-3 p-5">
        {state.problem ? (
          <Notice tone={state.problem.kind === "expired" ? "warn" : "problem"}>
            {problemText(state.problem)}
          </Notice>
        ) : null}
        {isSlow ? (
          <Notice>
            Not arrived yet? The code comes from <b>{state.sender}</b>. Check
            your junk or quarantine folder. Some company filters hold new
            senders for a few minutes.
          </Notice>
        ) : null}
        <form className="space-y-3" onSubmit={submit}>
          <InputOTP
            maxLength={CODE_LENGTH}
            pattern="^[0-9]*$"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            aria-label="6-digit code"
            disabled={locked}
            aria-invalid={state.problem?.kind === "wrong" ? true : undefined}
            value={code}
            onChange={setCode}
          >
            {Array.from({ length: CODE_LENGTH }, (_, index) => (
              <InputOTPSlot key={index} index={index} />
            ))}
          </InputOTP>
          <Button
            type="submit"
            className="h-10 w-full font-semibold"
            disabled={busy || locked}
          >
            Sign in
          </Button>
        </form>
        <div className="flex items-center justify-between gap-2 text-sm">
          <button
            type="button"
            className="font-medium text-primary underline underline-offset-2"
            onClick={onDifferentAddress}
          >
            Use a different address
          </button>
          {secondsLeft > 0 ? (
            <span className="text-muted-foreground">
              Send a new code in {secondsLeft}s
            </span>
          ) : (
            <button
              type="button"
              className="font-medium text-primary underline underline-offset-2 disabled:opacity-50"
              disabled={busy}
              onClick={onResend}
            >
              Send a new code
            </button>
          )}
        </div>
      </Card>
    </SignInFrame>
  );
}
